import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Copy, Link2, Loader2, RefreshCcw, ShieldCheck, Unlink2 } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getCalendarFeedToken,
  getOrCreateCalendarFeedToken,
  regenerateCalendarFeedToken,
  revokeCalendarFeedToken,
} from '@/lib/calendarFeedTokens';
import { useToast } from '@/hooks/use-toast';

const DEFAULT_CALENDAR_ID = 'primary';
const DEFAULT_EXPIRY_DAYS = 180;

type StatusTone = 'info' | 'success' | 'warning' | 'error';

interface StatusMessage {
  tone: StatusTone;
  message: string;
}

const statusCopy: Record<StatusTone, { title: string; bg: string; border: string; text: string }> = {
  info: {
    title: 'Info',
    bg: 'bg-blue-50',
    border: 'border-blue-200/70',
    text: 'text-blue-800',
  },
  success: {
    title: 'Active',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200/70',
    text: 'text-emerald-800',
  },
  warning: {
    title: 'Action needed',
    bg: 'bg-amber-50',
    border: 'border-amber-200/70',
    text: 'text-amber-800',
  },
  error: {
    title: 'Error',
    bg: 'bg-red-50',
    border: 'border-red-200/70',
    text: 'text-red-800',
  },
};

export function CalendarSyncCard() {
  const { toast } = useToast();
  const [userId, setUserId] = useState<string | null>(null);
  const [calendarId, setCalendarId] = useState<string>(DEFAULT_CALENDAR_ID);
  const [expiresInDays, setExpiresInDays] = useState<number>(DEFAULT_EXPIRY_DAYS);
  const [feedUrl, setFeedUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);

  const auth = useMemo(() => getAuth(), []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid ?? null);
    });
    return unsub;
  }, [auth]);

  useEffect(() => {
    if (!userId) {
      setFeedUrl('');
      setStatus({ tone: 'warning', message: 'Sign in to create an ICS feed link.' });
      return;
    }

    let isCancelled = false;
    const loadExisting = async () => {
      setIsLoading(true);
      try {
        const existing = await getCalendarFeedToken(userId, calendarId.trim() || DEFAULT_CALENDAR_ID);
        if (isCancelled) return;
        if (existing) {
          setFeedUrl(existing.feedUrl);
          setStatus({ tone: 'success', message: 'Active feed link found.' });
        } else {
          setFeedUrl('');
          setStatus({ tone: 'info', message: 'No active feed. Generate a link to start syncing.' });
        }
      } catch (error) {
        if (!isCancelled) {
          setFeedUrl('');
          setStatus({ tone: 'error', message: 'Could not load feed token. Try again.' });
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    loadExisting();
    return () => {
      isCancelled = true;
    };
  }, [userId, calendarId]);

  const guardCalendarId = useCallback(() => {
    const trimmed = calendarId.trim() || DEFAULT_CALENDAR_ID;
    setCalendarId(trimmed);
    if (!trimmed) {
      toast({ title: 'Calendar ID required', description: 'Enter a calendar ID to manage sync.', variant: 'destructive' });
    }
    return trimmed;
  }, [calendarId, toast]);

  const handleCreateOrFetch = useCallback(async () => {
    const targetCalendarId = guardCalendarId();
    if (!userId || !targetCalendarId) return;
    setIsLoading(true);
    try {
      const { feedUrl: url } = await getOrCreateCalendarFeedToken(userId, targetCalendarId, expiresInDays || undefined);
      setFeedUrl(url);
      setStatus({ tone: 'success', message: 'Feed link ready. Add it to your calendar app.' });
      toast({ title: 'ICS feed ready', description: 'Link generated and saved.' });
    } catch (error) {
      setStatus({ tone: 'error', message: 'Unable to generate feed link.' });
      toast({ title: 'Generation failed', description: 'Could not create the feed token.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [expiresInDays, guardCalendarId, toast, userId]);

  const handleRegenerate = useCallback(async () => {
    const targetCalendarId = guardCalendarId();
    if (!userId || !targetCalendarId) return;
    setIsLoading(true);
    try {
      const { feedUrl: url } = await regenerateCalendarFeedToken(userId, targetCalendarId, expiresInDays || undefined);
      setFeedUrl(url);
      setStatus({ tone: 'success', message: 'Feed link rotated.' });
      toast({ title: 'Token rotated', description: 'New ICS feed link generated.' });
    } catch (error) {
      setStatus({ tone: 'error', message: 'Unable to rotate feed link.' });
      toast({ title: 'Rotation failed', description: 'Could not regenerate the feed token.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [expiresInDays, guardCalendarId, toast, userId]);

  const handleRevoke = useCallback(async () => {
    const targetCalendarId = guardCalendarId();
    if (!userId || !targetCalendarId) return;
    setIsLoading(true);
    try {
      await revokeCalendarFeedToken(userId, targetCalendarId);
      setFeedUrl('');
      setStatus({ tone: 'warning', message: 'Feed revoked. Existing subscriptions will stop updating.' });
      toast({ title: 'Feed revoked', description: 'ICS feed link disabled.' });
    } catch (error) {
      setStatus({ tone: 'error', message: 'Unable to revoke feed link.' });
      toast({ title: 'Revoke failed', description: 'Could not revoke the feed token.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [guardCalendarId, toast, userId]);

  const handleCopy = useCallback(() => {
    if (!feedUrl) return;
    navigator.clipboard?.writeText(feedUrl);
    toast({ title: 'Copied', description: 'ICS feed URL copied to clipboard.' });
  }, [feedUrl, toast]);

  const renderStatus = () => {
    if (!status) return null;
    const palette = statusCopy[status.tone];
    return (
      <div className={`rounded-md border px-3 py-2 ${palette.bg} ${palette.border}`}>
        <div className={`text-xs font-semibold ${palette.text}`}>{palette.title}</div>
        <div className="text-xs text-foreground/80">{status.message}</div>
      </div>
    );
  };

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Link2 className="w-4 h-4" />
          External calendar sync (ICS)
        </CardTitle>
        <CardDescription>
          Generate a secure, user-scoped, read-only ICS feed link for this calendar. Add it to Google, Apple, or Outlook to keep events in sync.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="calendar-id">Calendar ID</Label>
            <Input
              id="calendar-id"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              placeholder="primary"
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground">Matches the calendarId stored on events (e.g., primary, galactic-main).</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="expires">Token expires (days)</Label>
            <Input
              id="expires"
              type="number"
              min={1}
              max={3650}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value) || DEFAULT_EXPIRY_DAYS)}
            />
            <p className="text-[11px] text-muted-foreground">Rotate periodically for security. Defaults to 180 days.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={handleCreateOrFetch} disabled={!userId || isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Generate or fetch link
          </Button>
          <Button variant="outline" onClick={handleRegenerate} disabled={!userId || isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Rotate token
          </Button>
          <Button variant="ghost" onClick={handleRevoke} disabled={!userId || isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlink2 className="w-4 h-4 mr-2" />}
            Revoke
          </Button>
        </div>

        {renderStatus()}

        <div className="space-y-1.5">
          <Label htmlFor="feed-url">ICS feed URL</Label>
          <div className="flex gap-2">
            <Input id="feed-url" value={feedUrl} readOnly placeholder="Generate a link to copy" className="flex-1" />
            <Button variant="outline" onClick={handleCopy} disabled={!feedUrl}>
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Use this URL when subscribing in your calendar app. It stays read-only.</p>
        </div>

        <Alert>
          <AlertTitle className="flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="w-4 h-4" /> How to add the feed
          </AlertTitle>
          <AlertDescription className="text-xs leading-relaxed space-y-1">
            <p>Google Calendar: Other calendars → Add by URL → paste the ICS link.</p>
            <p>Apple Calendar: File → New Calendar Subscription → paste the URL.</p>
            <p>Outlook: Add calendar → Subscribe from web → paste the URL.</p>
          </AlertDescription>
        </Alert>

        <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs space-y-1">
          <div className="font-semibold text-foreground/80">Spec status (Phase 1 live)</div>
          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
            <li>ICS export function deployed at `/calendar-feed/:userId/:calendarId/:token` (read-only).</li>
            <li>Tokens stored in `calendarFeedTokens`; rotation and revoke supported here; tokens are per-user + calendar.</li>
            <li>Events already UTC+timezone; ICS emits UID, DTSTAMP, LAST-MODIFIED, SEQUENCE.</li>
          </ul>
        </div>

        <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs space-y-1">
          <div className="font-semibold text-foreground/80">Next steps</div>
          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
            <li>Add token revoke/rotation history and audit log (`syncLogs`, 30d retention).</li>
            <li>Surface last export check + URL validator in Developer page.</li>
            <li>Add hosting rewrite `/calendar-feed/**` to production deploy.</li>
            <li>Phase 2: Google/Microsoft incremental sync with cursors + webhooks.</li>
          </ul>
          <a
            className="text-primary underline font-semibold"
            href="/docs/13_blueprint_mds/13-sync-spec.md"
            target="_blank"
            rel="noreferrer"
          >
            Read the full sync spec
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
